import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, MapPin, Phone } from 'lucide-react';
import { businessInfo, serviceAreas } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABEL = {
  general: 'General Plumbing',
  roof: 'Roof Plumbing',
  maintenance: 'Maintenance Plumbing',
  skylight: 'Skylight Installation',
};

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  address: '',
  serviceType: '',
  message: '',
};

const ContactDetails = () => (
  <div className="space-y-6 mb-10">
    <div className="flex items-start gap-4">
      <div className="bg-blue-50 p-3 rounded-lg">
        <Phone className="text-blue-600" size={24} />
      </div>
      <div>
        <h3 className="text-slate-900 font-semibold mb-1">Phone</h3>
        <a
          href={`tel:${businessInfo.phoneRaw}`}
          data-testid="contact-phone-link"
          className="text-slate-600 hover:text-blue-600 transition-colors duration-200"
        >
          {businessInfo.phone}
        </a>
      </div>
    </div>
    <div className="flex items-start gap-4">
      <div className="bg-blue-50 p-3 rounded-lg">
        <Mail className="text-blue-600" size={24} />
      </div>
      <div>
        <h3 className="text-slate-900 font-semibold mb-1">Email</h3>
        <a
          href={`mailto:${businessInfo.email}`}
          data-testid="contact-email-link"
          className="text-slate-600 hover:text-blue-600 transition-colors duration-200 break-all"
        >
          {businessInfo.email}
        </a>
      </div>
    </div>
    <div className="flex items-start gap-4">
      <div className="bg-blue-50 p-3 rounded-lg">
        <MapPin className="text-blue-600" size={24} />
      </div>
      <div>
        <h3 className="text-slate-900 font-semibold mb-1">Location</h3>
        <p className="text-slate-600">
          {businessInfo.address}, {businessInfo.city}, {businessInfo.state} {businessInfo.zipCode}
        </p>
      </div>
    </div>
  </div>
);

const ServiceAreasList = () => (
  <div data-testid="service-areas" className="bg-slate-50 rounded-xl p-6 border border-slate-200">
    <h3 className="text-2xl font-bold text-slate-900 mb-2">Areas We Serve</h3>
    <p className="text-slate-600 text-sm mb-5">
      Proudly servicing Melbourne and surrounding suburbs across every region.
    </p>
    <div className="space-y-5 max-h-[420px] overflow-y-auto pr-1">
      {serviceAreas.map((area) => (
        <div key={area.region}>
          <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-2">
            {area.region}
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {area.suburbs.map((suburb) => (
              <span
                key={suburb}
                className="inline-block bg-white border border-slate-200 text-slate-700 text-xs px-2.5 py-1 rounded-md"
              >
                {suburb}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FormField = ({ label, name, type = 'text', value, onChange, required, testId, placeholder }) => (
  <div>
    <label className="block text-slate-700 mb-2 font-medium">
      {label} {required && '*'}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
      placeholder={placeholder}
    />
  </div>
);

const ServiceSelect = ({ value, onChange }) => (
  <div>
    <label className="block text-slate-700 mb-2 font-medium">Service Required *</label>
    <select
      name="serviceType"
      value={value}
      onChange={onChange}
      required
      data-testid="contact-service-type"
      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
    >
      <option value="">Select a service</option>
      <option value="general">General Plumbing</option>
      <option value="roof">Roof Plumbing</option>
      <option value="maintenance">Maintenance Plumbing</option>
      <option value="skylight">Skylight Installation</option>
    </select>
  </div>
);

const MessageField = ({ value, onChange }) => (
  <div>
    <label className="block text-slate-700 mb-2 font-medium">Message</label>
    <textarea
      name="message"
      value={value}
      onChange={onChange}
      rows="4"
      data-testid="contact-message"
      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
      placeholder="Tell us about your plumbing needs..."
    />
  </div>
);

const useContactForm = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // `prev` is the functional setState callback parameter, not a closed-over variable.
  // `setFormData` is a stable React setState reference and does not need to be a dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  // `API`, `EMPTY_FORM`, `SERVICE_LABEL` are module-level constants and never change.
  // `setFormData`/`setSubmitting` are stable React setState references.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        serviceType: SERVICE_LABEL[formData.serviceType] || formData.serviceType,
      });
      toast.success("Thank you! We'll contact you within 24 hours.");
      setFormData(EMPTY_FORM);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Something went wrong. Please call us directly.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [formData]);

  return { formData, submitting, handleChange, handleSubmit };
};

const ContactForm = () => {
  const { formData, submitting, handleChange, handleSubmit } = useContactForm();

  return (
    <div data-testid="contact-form-container" className="bg-slate-50 rounded-2xl p-8 shadow-md border border-slate-200">
      <h3 className="text-2xl font-bold text-slate-900 mb-6">Request a Callback</h3>
      <form onSubmit={handleSubmit} className="space-y-5">
        <FormField label="Full Name" name="name" value={formData.name} onChange={handleChange} required testId="contact-name" placeholder="John Smith" />
        <FormField label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} required testId="contact-email" placeholder="john@example.com" />
        <FormField label="Phone Number" name="phone" type="tel" value={formData.phone} onChange={handleChange} required testId="contact-phone" placeholder="04xx xxx xxx" />
        <FormField label="Service Address" name="address" value={formData.address} onChange={handleChange} testId="contact-address" placeholder="123 Main St, Melbourne" />
        <ServiceSelect value={formData.serviceType} onChange={handleChange} />
        <MessageField value={formData.message} onChange={handleChange} />
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit"
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-md hover:shadow-lg"
        >
          {submitting ? 'Sending…' : 'Submit Request'}
        </button>
      </form>
    </div>
  );
};

const ContactSection = () => (
  <section id="contact" data-testid="contact-section" className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
          Contact
        </span>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Get In Touch</h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Ready to fix your plumbing issues? Contact us today for a free quote!
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <ContactDetails />
          <ServiceAreasList />
        </div>
        <ContactForm />
      </div>
    </div>
  </section>
);

export default ContactSection;
